import React, { useCallback, useMemo, useState } from 'react';
import { fetchDealEventAnimals, type DealEventAnimal } from '../data/api';

type NodeKey = 'farm' | 'feedlot' | 'abattoir' | 'other';

const NODES: { key: NodeKey; label: string }[] = [
  { key: 'farm', label: 'Farm' },
  { key: 'feedlot', label: 'Feedlot' },
  { key: 'abattoir', label: 'Abattoir' },
];

// The event-type prefix says where an event happened. Codes with no prefix
// (VACCINATION) are placed by their recorded location instead — and if that
// can't be resolved they go to "Other" rather than being guessed into a node,
// so nothing is silently shown at a place it didn't happen.
const NODE_BY_PREFIX: Record<string, NodeKey> = { FARM: 'farm', FEED: 'feedlot', ABAT: 'abattoir' };

// The node heading already says where it happened, so these read as what
// happened: "Shorn", not "Feedlot shear". Keep every code the backend can send
// (ingest EntityEventType) in here -- a missing one used to fall through to the
// event type's UUID and print it as the row title.
const EVENT_LABELS: Record<string, string> = {
  FARM_REG: 'Registered',
  FARM_BIR: 'Born',
  FARM_REC: 'Received',
  FARM_DIS: 'Dispatched',
  FARM_MOR: 'Mortality',
  FARM_SHE: 'Shorn',
  FARM_WEI: 'Weighed',
  FARM_BAL: 'Wool baled',
  FEED_REG: 'Registered',
  FEED_REC: 'Received',
  FEED_DIS: 'Dispatched',
  FEED_MOR: 'Mortality',
  FEED_WEI: 'Weighed',
  FEED_VAC: 'Vaccination',
  FEED_SHE: 'Shorn',
  FEED_BAL: 'Wool baled',
  FEED_WOO: 'Wool batch',
  ABAT_REG: 'Registered',
  ABAT_REC: 'Received',
  ABAT_DIS: 'Dispatched',
  ABAT_SLA: 'Slaughtered',
  ABAT_MOR: 'Mortality',
  VACCINATION: 'Vaccination',
  WOOL_ANA: 'Wool analysis',
};

/** A code the map has not caught up with yet, read as words: FEED_SHE -> "Shear". */
function humaniseEventCode(code: string): string {
  const tail = /^(FARM|FEED|ABAT)_/.test(code) ? code.slice(5) : code;
  const words = tail.replace(/_/g, ' ').toLowerCase().trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

function fmtNum(value?: number | null): string {
  return value == null ? '—' : new Intl.NumberFormat().format(value);
}

export type DealEventsSectionProps = {
  dealId: string;
  dealEvents?: any[];
  /** Value-chain nodes from /tracking/, used to place events with no type prefix. */
  glns?: any[];
};

export default function DealEventsSection({ dealId, dealEvents, glns }: DealEventsSectionProps) {
  const [openEvent, setOpenEvent] = useState<any | null>(null);
  const [animals, setAnimals] = useState<DealEventAnimal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const entityTypeByName = useMemo(() => {
    const map: Record<string, string> = {};
    (glns || []).forEach((gln: any) => {
      if (gln?.name) map[gln.name] = String(gln.type || '').toLowerCase();
    });
    return map;
  }, [glns]);

  const nodeFor = useCallback(
    (event: any): NodeKey => {
      const code = String(event?.entity_event_type_code || event?.event_type || '');
      const byPrefix = NODE_BY_PREFIX[code.split('_')[0]];
      if (byPrefix) return byPrefix;

      // The API says what kind of place the event happened at (be ≥ 2.19.4);
      // a vaccination captured at Damspruit lands on the feedlot, not "Other".
      const byType = String(event?.location_entity_type || '').toLowerCase();
      if (byType === 'feedlot' || byType === 'abattoir' || byType === 'farm') return byType;

      const where =
        event?.location_entity_name || event?.destination_entity_name || event?.source_entity_name;
      const type = entityTypeByName[where];
      if (type === 'feedlot') return 'feedlot';
      if (type === 'abattoir') return 'abattoir';
      if (type === 'farm') return 'farm';
      return 'other';
    },
    [entityTypeByName],
  );

  const grouped = useMemo(() => {
    const buckets: Record<NodeKey, any[]> = { farm: [], feedlot: [], abattoir: [], other: [] };
    (dealEvents || []).forEach((event) => buckets[nodeFor(event)].push(event));
    // Newest first, oldest at the bottom of each box. Undated events sort last
    // rather than jumping to the top on an empty string.
    (Object.keys(buckets) as NodeKey[]).forEach((key) => {
      buckets[key].sort((a, b) => {
        const left = String(a.event_date || '');
        const right = String(b.event_date || '');
        if (!left) return 1;
        if (!right) return -1;
        return right.localeCompare(left);
      });
    });
    return buckets;
  }, [dealEvents, nodeFor]);

  const openAnimals = useCallback(
    async (event: any) => {
      setOpenEvent(event);
      setAnimals([]);
      setError('');
      setLoading(true);
      try {
        setAnimals(await fetchDealEventAnimals(dealId, event.id));
      } catch (e: any) {
        setError(e?.message || 'Could not load the animals for this event.');
      } finally {
        setLoading(false);
      }
    },
    [dealId],
  );

  // Never fall back to `entity_event_type` -- it is the type's UUID, and an
  // unmapped code (FEED_SHE) printed it as the row title.
  const eventLabel = (event: any) => {
    const code = String(event?.entity_event_type_code || event?.event_type || '');
    return EVENT_LABELS[code] || humaniseEventCode(code) || 'Event';
  };

  const countOf = (event: any) => event?.animals_total_count ?? event?.animal_count ?? null;

  const visibleNodes = grouped.other.length
    ? [...NODES, { key: 'other' as NodeKey, label: 'Unplaced' }]
    : NODES;

  const totalEvents = (dealEvents || []).length;

  return (
    <div className="grid gap-4">
      <div>
        <h3 className="text-lg font-semibold text-avcs-text">Events</h3>
        <p className="mt-0.5 text-sm text-avcs-muted">
          What actually happened at each stage of the supply chain program. Select an event to see the animals involved.
        </p>
      </div>

      {totalEvents === 0 ? (
        <p className="rounded-xl border border-[#1e6bb824] bg-[#fbfeff] p-4 text-sm text-avcs-muted">
          No events recorded for this deal yet.
        </p>
      ) : (
        <div className={`grid gap-3 ${visibleNodes.length > 3 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'} sm:grid-cols-2`}>
          {visibleNodes.map(({ key, label }) => (
            <div key={key} className="flex flex-col rounded-xl border border-[#1e6bb829] bg-white">
              <div className="flex items-baseline justify-between gap-2 border-b border-[#1e6bb824] bg-[#f8fbff] px-4 py-2">
                <span className="text-sm font-semibold uppercase tracking-wide text-avcs-muted">{label}</span>
                <span className="text-xs text-avcs-muted">{grouped[key].length} event(s)</span>
              </div>

              {grouped[key].length === 0 ? (
                <p className="px-4 py-4 text-sm text-avcs-muted">Nothing recorded here.</p>
              ) : (
                <ul className="divide-y divide-[#1e6bb812]">
                  {grouped[key].map((event) => (
                    <li key={event.id}>
                      <button
                        type="button"
                        className="w-full px-4 py-3 text-left transition hover:bg-[#f8fbff]"
                        onClick={() => openAnimals(event)}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-semibold text-avcs-text">{eventLabel(event)}</span>
                          <span className="whitespace-nowrap text-sm text-avcs-muted">
                            {countOf(event) != null ? `${fmtNum(countOf(event))} head` : '—'}
                          </span>
                        </div>
                        <div className="mt-0.5 text-xs text-avcs-muted">{fmtDate(event.event_date)}</div>
                        {/* A vaccination row is meaningless without the medicine --
                            "Vaccination · 15 head" says nothing an operator can act on. */}
                        {event.vaccination_detail && (
                          <div className="mt-0.5 truncate text-xs text-avcs-text">
                            {[
                              event.vaccination_detail.medicine?.name || event.vaccination_detail.vaccination_type_display,
                              event.vaccination_detail.medicine?.brand_name,
                              event.vaccination_detail.dosage ? `${event.vaccination_detail.dosage} dose` : '',
                              event.vaccination_detail.batch_number ? `batch ${event.vaccination_detail.batch_number}` : '',
                            ].filter(Boolean).join(' · ')}
                          </div>
                        )}
                        {(event.source_entity_name || event.destination_entity_name) && (
                          <div className="mt-0.5 truncate text-xs text-avcs-muted">
                            {event.source_entity_name === event.destination_entity_name
                              ? event.destination_entity_name
                              : `${event.source_entity_name || '—'} → ${event.destination_entity_name || '—'}`}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {openEvent && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-[#0b223a66] px-4 py-8"
          onClick={() => setOpenEvent(null)}
        >
          <div
            className="flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-[#1e6bb824] bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-[#1e6bb824] px-4 py-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-avcs-text">{eventLabel(openEvent)}</h3>
                <p className="truncate text-sm text-avcs-muted">
                  {fmtDate(openEvent.event_date)}
                  {countOf(openEvent) != null ? ` · ${fmtNum(countOf(openEvent))} head` : ''}
                  {openEvent.destination_entity_name ? ` · ${openEvent.destination_entity_name}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="text-2xl leading-none text-avcs-muted hover:text-[#6b1010]"
                onClick={() => setOpenEvent(null)}
                aria-label="Close"
              >×</button>
            </div>

            <div className="overflow-auto p-4">
              {loading ? (
                <p className="text-sm text-avcs-muted">Loading animals…</p>
              ) : error ? (
                <p role="alert" className="rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">
                  {error}
                </p>
              ) : animals.length === 0 ? (
                <p className="text-sm text-avcs-muted">
                  No animals are linked to this event.
                  {countOf(openEvent) ? ' Its recorded count came from the uploaded file rather than matched animals.' : ''}
                </p>
              ) : (
                <>
                  {countOf(openEvent) != null && animals.length !== countOf(openEvent) && (
                    <p className="mb-3 rounded-lg border border-[#e7d5b9] bg-[#fbf4e7] px-3 py-2 text-xs text-[#6b4710]">
                      {fmtNum(animals.length)} animal(s) linked, but the event records {fmtNum(countOf(openEvent))}.
                      The difference is rows in the file that never matched an animal.
                    </p>
                  )}
                  <table className="min-w-full text-sm">
                    <thead className="border-b border-[#1e6bb824] bg-[#f8fbff]">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-avcs-muted">EID</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-avcs-muted">VID</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-avcs-muted">Species</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-avcs-muted">Sex</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-avcs-muted">Current location</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1e6bb812]">
                      {animals.map((animal) => (
                        <tr key={animal.id} className="hover:bg-[#f8fbff]">
                          <td className="px-3 py-2 font-medium text-avcs-text">{animal.eid || '—'}</td>
                          <td className="px-3 py-2">{animal.vid || '—'}</td>
                          <td className="px-3 py-2">{animal.species || '—'}</td>
                          <td className="px-3 py-2">{animal.sex || '—'}</td>
                          <td className="px-3 py-2 text-avcs-muted">{animal.current_entity || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
